import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ShoppingBag, Plus, Check, Loader2 } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { useCart, formatEUR } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Shop() {
  const { addItem, openDrawer, count } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [added, setAdded] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/shop/products`);
        const data = await res.json();
        setProducts(Array.isArray(data) ? data : []);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));
    return ["all", ...set];
  }, [products]);

  const shown = cat === "all" ? products : products.filter((p) => p.category === cat);

  const handleAdd = (p) => {
    addItem(p);
    setAdded(p.id);
    toast.success(`${p.name} added to your basket.`);
    setTimeout(() => setAdded((cur) => (cur === p.id ? null : cur)), 1400);
  };

  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="top-center" theme="dark" />
      <Header />

      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 lg:px-10 lg:pt-40">
        {/* Hero */}
        <motion.div
          data-testid="shop-hero"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-clan-accent">
            The Clan Store
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
            Wear the tartan. Carry the crest.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">
            A small, considered collection of clan merchandise — woven, cast and printed for the kindred the world over.
            Every order ships worldwide.
          </p>
        </motion.div>

        {/* Category filter */}
        {!loading && categories.length > 2 && (
          <div className="mt-10 flex flex-wrap gap-2" data-testid="shop-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`shop-filter-${c}`}
                onClick={() => setCat(c)}
                className={`rounded-md border px-4 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors ${
                  cat === c
                    ? "border-clan-accent bg-clan-accent/15 text-clan-ink"
                    : "border-clan-ink/12 text-clan-muted hover:border-clan-accent/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid place-items-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-clan-accent" />
          </div>
        ) : shown.length === 0 ? (
          <div className="mt-12 rounded-lg border border-dashed border-clan-ink/15 p-16 text-center">
            <p className="font-serif text-xl italic text-clan-muted">The store is being restocked. Check back soon.</p>
          </div>
        ) : (
          <div data-testid="shop-grid" className="mt-12 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p, idx) => {
              const soldOut = p.stock === 0;
              return (
                <motion.article
                  key={p.id}
                  data-testid={`product-card-${p.id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: (idx % 3) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="group flex flex-col"
                >
                  <div className="relative overflow-hidden rounded-lg bg-clan-ink/[0.04]">
                    {p.image_url && (
                      <img
                        src={p.image_url}
                        alt={p.name}
                        loading="lazy"
                        className="aspect-square w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105"
                      />
                    )}
                    {soldOut && (
                      <span className="absolute right-3 top-3 rounded-md bg-clan-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-clan-bg">
                        Sold out
                      </span>
                    )}
                  </div>

                  <div className="mt-5 flex flex-1 flex-col">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-clan-accent">{p.category}</span>
                    <h2 className="mt-2 font-serif text-xl leading-snug text-clan-ink">{p.name}</h2>
                    {p.description && (
                      <p className="mt-2 text-sm leading-relaxed text-clan-muted">{p.description}</p>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-5">
                      <span className="font-serif text-2xl text-clan-ink">{formatEUR(p.price)}</span>
                      <button
                        data-testid={`add-to-cart-${p.id}`}
                        disabled={soldOut}
                        onClick={() => handleAdd(p)}
                        className="flex items-center gap-2 rounded-md border border-clan-accent/50 bg-clan-accent/10 px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest text-clan-accent transition-colors duration-300 hover:bg-clan-accent/20 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {added === p.id ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                        {soldOut ? "Sold out" : added === p.id ? "Added" : "Add"}
                      </button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        )}
      </main>

      {/* Floating basket button */}
      {count > 0 && (
        <button
          data-testid="shop-open-cart-btn"
          onClick={openDrawer}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-md bg-clan-primary px-5 py-3.5 font-mono text-xs uppercase tracking-widest text-white shadow-xl transition-transform duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-clan-accent"
        >
          <ShoppingBag className="h-4 w-4" /> View basket ({count})
        </button>
      )}

      <Footer />
    </div>
  );
}
